import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ConnectionMode,
  ConnectionLineType,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import useStore from '../store/useStore';
import EntityNode from './EntityNode';
import EnumNode from './EnumNode';
import RelationshipEdge from './RelationshipEdge';
import { useT } from '../i18n';

const nodeTypes = {
  entity: EntityNode,
  enum: EnumNode,
};

const edgeTypes = {
  relationship: RelationshipEdge,
};

function getFieldIndex(handleId) {
  if (!handleId) return 0;
  const match = handleId.match(/^field-(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function isFieldHandle(handleId) {
  return typeof handleId === 'string' && handleId.startsWith('field-');
}

function isEnumHandle(handleId) {
  return typeof handleId === 'string' && handleId.startsWith('enum-');
}

function lcFirst(str) {
  return str ? str.charAt(0).toLowerCase() + str.slice(1) : str;
}

function isPrimitiveType(t) {
  return t === 'Long' || t === 'Integer' || t === 'long' || t === 'int';
}

// Decide what name + type the FK field on the owner side should become.
// - empty name → rename to lowercase(referencedName)
// - existing name ends in "Id" with primitive type → strip "Id" suffix
// - otherwise: keep name, only change type
function buildFieldUpdate(currentField, referencedName) {
  const updates = { type: referencedName };
  const name = currentField?.name || '';
  if (!name.trim()) {
    updates.name = lcFirst(referencedName);
  } else if (/Id$/.test(name) && isPrimitiveType(currentField.type)) {
    const stripped = name.replace(/Id$/, '');
    updates.name = stripped || lcFirst(referencedName);
  }
  return updates;
}

// Find existing edges that occupy any handle of the given field row.
function findRowEdges(edges, nodeId, fieldIdx) {
  const prefix = `field-${fieldIdx}-`;
  return edges.filter((e) => {
    if (e.source === nodeId && typeof e.sourceHandle === 'string' && e.sourceHandle.startsWith(prefix)) return true;
    if (e.target === nodeId && typeof e.targetHandle === 'string' && e.targetHandle.startsWith(prefix)) return true;
    return false;
  });
}

export default function Canvas() {
  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);
  const theme = useStore((s) => s.theme);
  const readOnly = useStore((s) => s.readOnly);
  const onNodesChange = useStore((s) => s.onNodesChange);
  const onEdgesChange = useStore((s) => s.onEdgesChange);
  const clearSelection = useStore((s) => s.clearSelection);
  const addEntityNode = useStore((s) => s.addEntityNode);
  const addEnumNode = useStore((s) => s.addEnumNode);
  const { screenToFlowPosition } = useReactFlow();

  const addEdge = useStore((s) => s.addEdge);
  const updateField = useStore((s) => s.updateField);
  const deleteEdges = useStore((s) => s.deleteEdges);
  const t = useT();

  const [pendingReplace, setPendingReplace] = useState(null);

  const applyConnection = useCallback(({ fieldUpdate, edge }) => {
    if (fieldUpdate) {
      updateField(fieldUpdate.nodeId, fieldUpdate.fieldIdx, fieldUpdate.updates);
    }
    addEdge(edge);
  }, [addEdge, updateField]);

  const handleConfirmReplace = useCallback(() => {
    if (!pendingReplace) return;
    deleteEdges(pendingReplace.conflictIds);
    applyConnection(pendingReplace);
    setPendingReplace(null);
  }, [pendingReplace, deleteEdges, applyConnection]);

  const handleCancelReplace = useCallback(() => setPendingReplace(null), []);

  const onConnect = useCallback(
    (connection) => {
      const storeNodes = useStore.getState().nodes;
      const storeEdges = useStore.getState().edges;
      const sourceNode = storeNodes.find((n) => n.id === connection.source);
      const targetNode = storeNodes.find((n) => n.id === connection.target);
      if (!sourceNode || !targetNode) return;

      const sourceIsEnum = sourceNode.data?.tableType === 'enum';
      const targetIsEnum = targetNode.data?.tableType === 'enum';

      // ===== Enum involved =====
      if (sourceIsEnum || targetIsEnum) {
        const enumNode = sourceIsEnum ? sourceNode : targetNode;
        const entityNode = sourceIsEnum ? targetNode : sourceNode;
        const enumHandle = sourceIsEnum ? connection.sourceHandle : connection.targetHandle;
        const entityHandle = sourceIsEnum ? connection.targetHandle : connection.sourceHandle;

        if (!isEnumHandle(enumHandle) || !isFieldHandle(entityHandle)) return;

        const entityFieldIdx = getFieldIndex(entityHandle);
        if (entityFieldIdx === 0) return;

        const entityField = entityNode.data?.fields?.[entityFieldIdx];
        if (!entityField) return;

        const updates = buildFieldUpdate(entityField, enumNode.data.name);
        const fieldUpdate = { nodeId: entityNode.id, fieldIdx: entityFieldIdx, updates };

        const newEdge = {
          id: `e-${connection.source}-${connection.target}-${Date.now()}`,
          source: connection.source,
          sourceHandle: connection.sourceHandle,
          target: connection.target,
          targetHandle: connection.targetHandle,
          type: 'relationship',
          data: { sourceType: 'Enum', targetType: 'Enum' },
        };

        const conflicts = findRowEdges(storeEdges, entityNode.id, entityFieldIdx);
        if (conflicts.length > 0) {
          setPendingReplace({
            conflictIds: conflicts.map((e) => e.id),
            fieldUpdate,
            edge: newEdge,
            ownerName: entityNode.data?.name,
            referencedName: enumNode.data?.name,
          });
          return;
        }
        applyConnection({ fieldUpdate, edge: newEdge });
        return;
      }

      // ===== Entity → Entity =====
      if (!isFieldHandle(connection.sourceHandle) || !isFieldHandle(connection.targetHandle)) return;

      const sourceFieldIdx = getFieldIndex(connection.sourceHandle);
      const targetFieldIdx = getFieldIndex(connection.targetHandle);
      const sourceIsId = sourceFieldIdx === 0;
      const targetIsId = targetFieldIdx === 0;

      if (!sourceIsId && !targetIsId) return;

      // Both id → simple 1:1, no field changes.
      if (sourceIsId && targetIsId) {
        addEdge({
          id: `e-${connection.source}-${connection.target}-${Date.now()}`,
          source: connection.source,
          sourceHandle: connection.sourceHandle,
          target: connection.target,
          targetHandle: connection.targetHandle,
          type: 'relationship',
          data: { sourceType: 'One', targetType: 'One' },
        });
        return;
      }

      const referencedNode = sourceIsId ? sourceNode : targetNode;
      const ownerNode = sourceIsId ? targetNode : sourceNode;
      const ownerFieldIdx = sourceIsId ? targetFieldIdx : sourceFieldIdx;
      const ownerField = ownerNode.data?.fields?.[ownerFieldIdx];
      if (!ownerField) return;

      const updates = buildFieldUpdate(ownerField, referencedNode.data.name);
      const fieldUpdate = { nodeId: ownerNode.id, fieldIdx: ownerFieldIdx, updates };

      const newEdge = {
        id: `e-${connection.source}-${connection.target}-${Date.now()}`,
        source: connection.source,
        sourceHandle: connection.sourceHandle,
        target: connection.target,
        targetHandle: connection.targetHandle,
        type: 'relationship',
        data: {
          sourceType: sourceIsId ? 'One' : 'Many',
          targetType: sourceIsId ? 'Many' : 'One',
        },
      };

      const conflicts = findRowEdges(storeEdges, ownerNode.id, ownerFieldIdx);
      if (conflicts.length > 0) {
        setPendingReplace({
          conflictIds: conflicts.map((e) => e.id),
          fieldUpdate,
          edge: newEdge,
          ownerName: ownerNode.data?.name,
          referencedName: referencedNode.data?.name,
        });
        return;
      }
      applyConnection({ fieldUpdate, edge: newEdge });
    },
    [addEdge, applyConnection]
  );

  const onPaneClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('application/reactflow-type');
      if (!type) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      if (type === 'entity') addEntityNode(position);
      else if (type === 'enum') addEnumNode(position);
    },
    [screenToFlowPosition, addEntityNode, addEnumNode]
  );

  const defaultEdgeOptions = useMemo(
    () => ({
      type: 'relationship',
    }),
    []
  );

  return (
    <div className="canvas-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onPaneClick={onPaneClick}
        onDragOver={readOnly ? undefined : onDragOver}
        onDrop={readOnly ? undefined : onDrop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView={false}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        deleteKeyCode={readOnly ? null : 'Delete'}
        multiSelectionKeyCode="Control"
        connectionLineType={ConnectionLineType.SmoothStep}
        connectionMode={ConnectionMode.Loose}
        snapToGrid
        snapGrid={[15, 15]}
      >
        <Background
          color={theme === 'dark' ? '#1a1a22' : '#e8e8e8'}
          gap={44}
          size={0.7}
          variant="lines"
        />
        <Controls />
        <MiniMap
          nodeColor={(n) =>
            n.data?.tableType === 'enum' ? '#f0ad4e' : '#5b9bd5'
          }
          style={{ background: theme === 'dark' ? '#1a1a2e' : '#f5f5f5' }}
        />
      </ReactFlow>

      {pendingReplace && (
        <div className="confirm-overlay" onClick={handleCancelReplace}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="var(--accent)" strokeWidth="2.5" fill="rgba(74,144,217,0.08)" />
                <path d="M16 18l5-5M21 13l5 5M21 13v22M32 30l-5 5M27 35l-5-5M27 35V13" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            <h3 className="confirm-title">{t('canvas.replaceRelationTitle')}</h3>
            <p className="confirm-desc">{t('canvas.replaceRelationConfirm')}</p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={handleCancelReplace}>
                {t('profile.cancel')}
              </button>
              <button
                className="confirm-delete"
                style={{ background: 'var(--accent)' }}
                onClick={handleConfirmReplace}
              >
                {t('canvas.replaceRelationOk')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
